/**
 * CancelBookingModal — asks for a cancellation reason before calling the
 * cancel API. Renders as an inline modal overlay.
 *
 * Reason is a closed-set dropdown plus an optional free-text note.
 * On confirm, calls `onConfirm(reason, note)` — the caller is responsible for
 * the actual API call so existing cancel flows only need a one-line change.
 */
import { X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

export type CancelReason =
  | 'customer_requested'
  | 'no_show'
  | 'overbooking'
  | 'weather'
  | 'emergency'
  | 'other';

const REASON_LABELS: Record<CancelReason, string> = {
  customer_requested: 'Customer requested',
  no_show: 'No-show',
  overbooking: 'Overbooking',
  weather: 'Weather',
  emergency: 'Emergency',
  other: 'Other',
};

interface Props {
  open: boolean;
  petName?: string | undefined;
  // Allow async handlers — callers often fire-and-forget inside the modal.
  onConfirm: (reason: CancelReason, note: string) => void | Promise<void>;
  onCancel: () => void;
}

export function CancelBookingModal({ open, petName, onConfirm, onCancel }: Props) {
  const [reason, setReason] = useState<CancelReason>('customer_requested');
  const [note, setNote] = useState('');
  const selectRef = useRef<HTMLSelectElement>(null);

  // Reset state when modal opens; focus the select for keyboard users.
  useEffect(() => {
    if (open) {
      setReason('customer_requested');
      setNote('');
      setTimeout(() => selectRef.current?.focus(), 0);
    }
  }, [open]);

  if (!open) return null;

  function handleConfirm() {
    onConfirm(reason, note.trim());
  }

  return (
    /* Backdrop */
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Cancel booking"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      {/* Panel */}
      <form
        className="w-full max-w-sm rounded-2xl border border-border bg-background p-6 shadow-xl"
        onSubmit={(e) => {
          e.preventDefault();
          handleConfirm();
        }}
      >
        {/* Header */}
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-foreground">
              Cancel booking{petName ? ` — ${petName}` : ''}
            </h2>
            <p className="mt-0.5 text-sm text-muted-foreground">
              Please select a reason before proceeding.
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            aria-label="Close"
            className="mt-0.5 shrink-0 rounded-md p-1 text-muted-foreground hover:bg-secondary hover:text-foreground"
          >
            <X className="size-4" strokeWidth={1.75} />
          </button>
        </div>

        {/* Reason select */}
        <div className="space-y-1.5">
          <label
            htmlFor="cancel-reason"
            className="text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
          >
            Reason
          </label>
          <select
            id="cancel-reason"
            ref={selectRef}
            value={reason}
            onChange={(e) => setReason(e.target.value as CancelReason)}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30"
          >
            {(Object.keys(REASON_LABELS) as CancelReason[]).map((r) => (
              <option key={r} value={r}>
                {REASON_LABELS[r]}
              </option>
            ))}
          </select>
        </div>

        {/* Optional note */}
        <div className="mt-3 space-y-1.5">
          <label
            htmlFor="cancel-note"
            className="text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
          >
            Additional notes{' '}
            <span className="normal-case font-normal text-muted-foreground/70">(optional)</span>
          </label>
          <textarea
            id="cancel-note"
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Any extra details…"
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30 resize-none"
          />
        </div>

        {/* Actions */}
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md border border-border px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-secondary"
          >
            Keep booking
          </button>
          <button
            type="submit"
            className="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-2 text-sm font-medium text-destructive hover:bg-destructive/10"
          >
            Cancel booking
          </button>
        </div>
      </form>
    </div>
  );
}
